/**
 * What a workspace does with a live access re-check — desktop `WorkspaceView`
 * and the mobile workspace alike (I262, #1260).
 *
 * The permission route never answers 'none' in a 200: a folder the caller can
 * no longer see is a 403 (and a gone one a 404) — that IS the revoke. A 200
 * 'hidden' (a locked shell) is too. Anything else that fails — a 429 (what the
 * 2026-10-21 read flood produced), a 5xx, a phone's flaky network — is a failed
 * CHECK, not a revoke: stay; the next change re-checks and every write is
 * judged by the server anyway.
 *
 * #1406: a 'hidden' folder is still a project for someone who reads its config (a
 * hallway project) — for a 'hidden' answer the caller passes `opensAsProject` (the
 * server's answer as of now): `true` stays, read-only; anything else leaves, as a
 * 'hidden' answer always did. A READABLE folder never leaves on the project rule:
 * one whose config read was taken away stays open until it is next opened (the
 * server refuses that open, and its writes meanwhile) — a documented residual, since
 * telling a failed check from "no" on every path is not worth its moving parts (P15).
 */
export type WorkspaceAccessOutcome = { kind: 'leave' } | { kind: 'stay' } | { kind: 'permission'; permission: string }

export function workspaceAccessOutcome(result: { permission: string | null; opensAsProject?: boolean } | { error: unknown }): WorkspaceAccessOutcome {
  if ('error' in result) {
    const status = (result.error as { status?: number } | null)?.status
    return status === 403 || status === 404 ? { kind: 'leave' } : { kind: 'stay' }
  }
  const p = result.permission
  if (p === 'hidden' && result.opensAsProject === true) return { kind: 'permission', permission: p }
  if (!p || p === 'hidden' || p === 'none') return { kind: 'leave' }
  return { kind: 'permission', permission: p }
}
